import type { RawTrawledPaper } from "@/lib/trawl-utils";
import type { ConceptScope } from "@/types/concept";

/** One seed's fetched network, per direction. "citations" = papers that cite the seed. */
export interface SeedNetwork {
  citations: RawTrawledPaper[];
  references: RawTrawledPaper[];
  recommendations: RawTrawledPaper[];
  /** Works the seed's citers also cite; empty unless the seed has no references. */
  cocited: RawTrawledPaper[];
}
export type Direction = keyof SeedNetwork;
export const DIRECTIONS: Direction[] = ["citations", "references", "recommendations", "cocited"];

export interface PreviewPaper {
  paper: RawTrawledPaper;
  directions: Set<Direction>;
}

export function paperKey(p: RawTrawledPaper): string {
  return p.paper_id ?? p.doi ?? p.title.toLowerCase();
}

export function mergeNetworks(nets: SeedNetwork[]): Map<string, PreviewPaper> {
  const out = new Map<string, PreviewPaper>();
  for (const net of nets) {
    for (const dir of DIRECTIONS) {
      for (const paper of net[dir] ?? []) {
        const key = paperKey(paper);
        const hit = out.get(key);
        if (hit) hit.directions.add(dir);
        else out.set(key, { paper, directions: new Set([dir]) });
      }
    }
  }
  return out;
}

/** Spec §2: papers with no year are always in range. */
export function inYearRange(year: number | null | undefined, scope: ConceptScope | null | undefined): boolean {
  if (year == null || !scope) return true;
  if (scope.year_min != null && year < scope.year_min) return false;
  return !(scope.year_max != null && year > scope.year_max);
}

export interface PreviewStats {
  inRange: number;
  outOfRange: number;
  spanFrom: number | null;
  spanTo: number | null;
  citeSeedsPct: number;
  citedBySeedsPct: number;
}

const pct = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);

export function previewStats(merged: Map<string, PreviewPaper>, scope: ConceptScope | null | undefined): PreviewStats {
  const inRange = [...merged.values()].filter((x) => inYearRange(x.paper.year, scope));
  const years = inRange.map((x) => x.paper.year).filter((y): y is number => y != null).sort((a, b) => a - b);
  const at = (q: number) => (years.length ? years[Math.round(q * (years.length - 1))] : null);
  return {
    inRange: inRange.length,
    outOfRange: merged.size - inRange.length,
    spanFrom: at(0.1),
    spanTo: at(0.9),
    citeSeedsPct: pct(inRange.filter((x) => x.directions.has("citations")).length, inRange.length),
    citedBySeedsPct: pct(inRange.filter((x) => x.directions.has("references")).length, inRange.length),
  };
}

/** A paper the grader can judge: S2 stubs with no abstract or year (e.g. a journal
 *  name as the title) grade 0 and make the sample look worse than the run will. */
export const gradable = (x: PreviewPaper) => x.paper.year != null && !!x.paper.abstract?.trim();

export type Band = 0 | 1 | 2 | 3;

/** n in-range papers, round-robin across directions, spread across years within each.
 *  Gradable papers first; the rest only fill in when there aren't n of them.
 *  With ②'s quick-check bands (≥ n gradable papers banded) the sample is drawn
 *  across the range instead — 3 hits, 3 borderline, 2 misses for n = 8 — so ③
 *  tests the grader on all three, not on the network's (often off-topic) mix.
 *  A short band passes its shortfall to the next band down. */
export function pickSample(
  merged: Map<string, PreviewPaper>, scope: ConceptScope | null | undefined, n = 8,
  bands?: Record<string, Band | null>,
): PreviewPaper[] {
  const inRange = [...merged.values()].filter((x) => inYearRange(x.paper.year, scope));
  const pool = inRange.filter(gradable);
  const bandOf = (x: PreviewPaper) => bands?.[paperKey(x.paper)];
  if (bands && pool.filter((x) => bandOf(x) != null).length >= n) {
    const groups = [
      pool.filter((x) => bandOf(x) === 3),
      pool.filter((x) => bandOf(x) === 2),
      pool.filter((x) => { const b = bandOf(x); return b === 0 || b === 1; }),
    ];
    const hits = Math.round((n * 3) / 8);
    const quotas = [hits, hits, n - 2 * hits];
    const picked: PreviewPaper[] = [];
    let carry = 0;
    groups.forEach((g, i) => {
      const want = quotas[i] + carry;
      // spreadPick's stride can skip items when n >= pool size; take a short band whole.
      const got = g.length <= want ? g : spreadPick(g, want);
      carry = quotas[i] + carry - got.length;
      picked.push(...got);
    });
    if (picked.length < n) {
      const have = new Set(picked.map((x) => paperKey(x.paper)));
      picked.push(...spreadPick(pool.filter((x) => !have.has(paperKey(x.paper))), n - picked.length));
    }
    return picked.sort((a, b) => (a.paper.year ?? 0) - (b.paper.year ?? 0) || paperKey(a.paper).localeCompare(paperKey(b.paper)));
  }
  const picked = spreadPick(pool, n);
  return picked.length < n ? picked.concat(spreadPick(inRange.filter((x) => !gradable(x)), n - picked.length)) : picked;
}

export function spreadPick(pool: PreviewPaper[], n: number): PreviewPaper[] {
  // Co-cited only takes a share when a seed has any (rare outside classic humanities).
  const dirs = DIRECTIONS.filter((d) => d !== "cocited" || pool.some((x) => x.directions.has(d)));
  const byDir = dirs.map((dir) =>
    pool
      .filter((x) => x.directions.has(dir))
      .sort((a, b) => (a.paper.year ?? 0) - (b.paper.year ?? 0) || paperKey(a.paper).localeCompare(paperKey(b.paper))),
  );
  const picked: PreviewPaper[] = [];
  const seen = new Set<string>();
  // Evenly spaced positions within each direction, interleaved.
  for (let round = 0; picked.length < n && byDir.some((l) => l.length > round); round++) {
    for (const list of byDir) {
      if (picked.length >= n) break;
      const stride = Math.max(1, Math.floor(list.length / Math.ceil(n / dirs.length)));
      const item = list[round * stride] ?? list[round];
      if (item && !seen.has(paperKey(item.paper))) {
        seen.add(paperKey(item.paper));
        picked.push(item);
      }
    }
  }
  return picked;
}

export function quotaNote(inRange: number, remaining: number | null): string | null {
  return remaining != null && inRange > remaining ? `We'll grade ${remaining} now (your plan's limit).` : null;
}

export interface SeedReach { unique: number; inRange: number; outOfRange: number }

function papersOf(net: SeedNetwork): Map<string, RawTrawledPaper> {
  const out = new Map<string, RawTrawledPaper>();
  for (const dir of DIRECTIONS) for (const paper of net[dir] ?? []) out.set(paperKey(paper), paper);
  return out;
}

/** Per seed: in-range papers its network brings that no OTHER ticked seed brings
 *  (so the numbers add up instead of overlapping), plus its in/out-of-range size.
 *  `nets` is keyed by seed key; unticked seeds are compared against all ticked ones. */
export function seedReach(
  nets: Record<string, SeedNetwork>, ticked: string[], scope: ConceptScope | null | undefined,
): Record<string, SeedReach> {
  const sets = Object.fromEntries(Object.entries(nets).map(([k, n]) => [k, papersOf(n)]));
  const out: Record<string, SeedReach> = {};
  for (const [key, papers] of Object.entries(sets)) {
    const others = ticked.filter((t) => t !== key && sets[t]).map((t) => sets[t]);
    const r = { unique: 0, inRange: 0, outOfRange: 0 };
    for (const [pk, paper] of papers) {
      if (!inYearRange(paper.year, scope)) { r.outOfRange++; continue; }
      r.inRange++;
      if (!others.some((o) => o.has(pk))) r.unique++;
    }
    out[key] = r;
  }
  return out;
}

export interface YearBucket { from: number; to: number; count: number; inRange: boolean }

const bucketInRange = (from: number, to: number, scope: ConceptScope | null | undefined) =>
  !(scope?.year_min != null && to < scope.year_min) && !(scope?.year_max != null && from > scope.year_max);

/** Year histogram over every preview paper (in and out of range); undated papers are skipped. */
export function yearHistogram(
  merged: Map<string, PreviewPaper>, scope: ConceptScope | null | undefined, bucket = 5,
): YearBucket[] {
  const years = [...merged.values()].map((x) => x.paper.year).filter((y): y is number => y != null);
  if (!years.length) return [];
  const lo = Math.floor(Math.min(...years) / bucket) * bucket;
  const hi = Math.floor(Math.max(...years) / bucket) * bucket;
  const out: YearBucket[] = [];
  for (let from = lo; from <= hi; from += bucket) {
    out.push({ from, to: from + bucket - 1, count: 0, inRange: bucketInRange(from, from + bucket - 1, scope) });
  }
  for (const y of years) out[Math.floor((y - lo) / bucket)].count++;
  return out;
}

export interface ConnectionSplit { seedsCite: number; citeSeeds: number; similar: number }

/** How in-range papers connect: "your seeds cite" (references + co-cited), "cite your
 *  seeds" (citations), "similar" (recommendations). A paper reached several ways counts
 *  in each; percentages are normalised to sum to 100. */
export function connectionSplit(merged: Map<string, PreviewPaper>, scope: ConceptScope | null | undefined): ConnectionSplit {
  let seedsCite = 0, citeSeeds = 0, similar = 0;
  for (const x of merged.values()) {
    if (!inYearRange(x.paper.year, scope)) continue;
    if (x.directions.has("references") || x.directions.has("cocited")) seedsCite++;
    if (x.directions.has("citations")) citeSeeds++;
    if (x.directions.has("recommendations")) similar++;
  }
  const total = seedsCite + citeSeeds + similar;
  if (!total) return { seedsCite: 0, citeSeeds: 0, similar: 0 };
  const a = Math.round((seedsCite / total) * 100);
  const b = Math.round((citeSeeds / total) * 100);
  return { seedsCite: a, citeSeeds: b, similar: Math.max(0, 100 - a - b) };
}

const REVIEW = /\b(review|reviews|meta-analys[ie]s|overview|survey)\b/i;

export const BEST_MATCH = "Closest to your question";

/** Seed tags; discovery's full reason sentence goes in a tooltip. The first tag
 *  says how the paper relates to the question (the selector's tier); Review /
 *  Classic / Recent ride alongside it and never replace it. The selector's #1
 *  pick, when it's about the subject, leads with BEST_MATCH: several seeds
 *  can share "About your subject", so the one most on the question gets its own.
 *  Candidates cached before tiers shipped have none and keep the old single tag. */
export function seedTags(
  c: { title: string; journal: string | null; year: number | null; tier?: SeedTier | null; rank?: number | null },
  nowYear: number,
): string[] {
  const review = REVIEW.test(c.title) || REVIEW.test(c.journal ?? "");
  const classic = c.year != null && c.year <= nowYear - 20;
  const recent = c.year != null && c.year >= nowYear - 3;
  if (!c.tier) {
    if (review) return ["Review — wide reach"];
    if (classic) return ["Classic"];
    if (recent) return ["Recent"];
    return ["Squarely on your question"];
  }
  const out = [c.tier === "core" ? (c.rank === 1 ? BEST_MATCH : "About your subject") : "Background"];
  if (review) out.push("Review");
  if (classic) out.push("Classic");
  if (recent) out.push("Recent");
  return out;
}

export type SeedTier = "core" | "context";
